<template>
    <Head title="Edit Schedule" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div>
                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Edit Checklist Schedule</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Modify checklist generation schedule</p>
            </div>
            <Link
                :href="route('checklists.schedules.show', schedule.id)"
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
                Back to Schedule
            </Link>
        </div>

        <!-- Form -->
        <form @submit.prevent="submit" class="space-y-6">
            <!-- Basic Information -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Schedule Name -->
                    <div class="md:col-span-2">
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Schedule Name <span class="text-red-500">*</span>
                        </label>
                        <input
                            v-model="form.title"
                            type="text"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        />
                        <p v-if="errors.title" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.title }}</p>
                    </div>

                    <!-- Description -->
                    <div class="md:col-span-2">
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            v-model="form.description"
                            rows="2"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                    </div>

                    <!-- Template -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Checklist Template <span class="text-red-500">*</span>
                        </label>
                        <br>
                        <select
                            v-model="form.template_id"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        >
                            <option value="">Select Template</option>
                            <option v-for="template in templates" :key="template.id" :value="template.id">
                                {{ template.title }} ({{ template.template_code }})
                            </option>
                        </select>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Select the checklist template that will be used for this schedule
                        </p>
                        <p v-if="errors.template_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.template_id }}</p>
                    </div>

                    <!-- SLA Hours and Minutes -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Completion SLA <span class="text-red-500">*</span>
                        </label>
                        <div class="grid grid-cols-2 gap-2">
                            <div>
                                <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                <input
                                    v-model.number="form.sla_hours"
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                    @keypress="preventDecimal"
                                    @input="stripDecimal($event, 'sla_hours')"
                                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                <input
                                    v-model.number="form.sla_minutes"
                                    type="number"
                                    min="0"
                                    max="59"
                                    step="1"
                                    placeholder="0"
                                    @keypress="preventDecimal"
                                    @input="stripDecimal($event, 'sla_minutes')"
                                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Time allowed to complete checklist after generation
                        </p>
                        <p v-if="slaError" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ slaError }}</p>
                    </div>

                    <!-- Expires After Hours and Minutes -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Expires After (Optional)
                        </label>
                        <div class="grid grid-cols-2 gap-2">
                            <div>
                                <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                <input
                                    v-model.number="form.expires_hours"
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                    @keypress="preventDecimal"
                                    @input="stripDecimal($event, 'expires_hours')"
                                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                <input
                                    v-model.number="form.expires_minutes"
                                    type="number"
                                    min="0"
                                    max="59"
                                    step="1"
                                    placeholder="0"
                                    @keypress="preventDecimal"
                                    @input="stripDecimal($event, 'expires_minutes')"
                                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            If set, checklist will automatically expire after this time. Leave blank for no expiry.
                        </p>
                        <p v-if="expiresError" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ expiresError }}</p>
                    </div>
                </div>
            </div>

            <!-- Schedule Configuration -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Schedule Configuration</h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Recurrence Pattern -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Recurrence Pattern <span class="text-red-500">*</span>
                        </label>
                        <select
                            v-model="form.recurrence_pattern"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        >
                            <option value="hourly">Hourly</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="yearly">Yearly</option>
                        </select>
                        <p v-if="errors.recurrence_pattern" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.recurrence_pattern }}</p>
                    </div>

                    <!-- Interval -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Every <span class="text-red-500">*</span>
                        </label>
                        <div class="flex items-center gap-2">
                            <input
                                v-model.number="form.interval"
                                type="number"
                                min="1"
                                class="mt-1 block w-24 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                required
                            />
                            <span class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                {{ getIntervalLabel() }}
                            </span>
                        </div>
                        <p v-if="errors.interval" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.interval }}</p>
                    </div>

                    <!-- Start Date -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Start Date <span class="text-red-500">*</span>
                        </label>
                        <input
                            v-model="form.start_date"
                            type="date"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        />
                        <p v-if="errors.start_date" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.start_date }}</p>
                    </div>

                    <!-- Start Time -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Start Time <span class="text-red-500">*</span>
                        </label>
                        <input
                            v-model="form.start_time"
                            type="time"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        />
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Time shown in {{ timezoneInfo.display_name }} ({{ timezoneInfo.gmt_offset }})
                        </p>
                        <p v-if="errors.start_time" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.start_time }}</p>
                    </div>

                    <!-- End Date -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            End Date (Optional)
                        </label>
                        <input
                            v-model="form.end_date"
                            type="date"
                            :min="form.start_date"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Leave blank for indefinite schedule</p>
                    </div>

                    <!-- Next Execution Preview -->
                    <div v-if="nextExecutionPreview" class="md:col-span-2">
                        <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                            <p class="text-sm text-blue-800 dark:text-blue-200">
                                <span class="font-medium">Next Execution:</span> {{ nextExecutionPreview }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Location and Asset -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Location and Asset (Optional)</h2>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Linking a location or asset enables deeper reporting analytics to track performance, completion rates, and compliance metrics. Leave blank if you don't want to link this schedule with any specific location or asset.
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Location -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Select Location
                        </label>
                        <select
                            v-model="form.location_id"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                            <option value="">No Location</option>
                            <option v-for="location in locations" :key="location.id" :value="location.id">
                                {{ location.name }}
                            </option>
                        </select>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            To schedule checklists for multiple locations, create separate schedules for each location
                        </p>
                    </div>

                    <!-- Asset -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Select Asset
                        </label>
                        <select
                            v-model="form.asset_id"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                            <option value="">No Asset</option>
                            <option v-for="asset in assets" :key="asset.id" :value="asset.id">
                                {{ asset.name }} ({{ asset.asset_tag }})
                            </option>
                        </select>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Link this checklist to a specific asset for asset-based maintenance tracking
                        </p>
                    </div>
                </div>
            </div>

            <!-- Checklist Manager Access Control -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Checklist Manager Access Control</h2>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Control which Checklist Managers can access and administer checklists created by this schedule. If no managers are selected, all Checklist Managers will be able to access and take action on these checklists and view their progress.
                </p>
                <div class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-500 rounded-md p-3 mb-4">
                    <p class="text-sm text-blue-700 dark:text-blue-300">
                        <strong>Use Case:</strong> Restricting access to specific managers is useful when checklists contain confidential information or require specialized oversight. If left blank, all users with the Checklist Manager role will have access.
                    </p>
                </div>

                <!-- Checklist Manager Selection -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select Checklist Managers (Optional)
                    </label>
                    <div class="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2">
                        <label v-for="user in checklistManagerUsers" :key="user.id" class="flex items-center text-xs">
                            <input
                                v-model="form.checklist_manager_ids"
                                type="checkbox"
                                :value="user.id"
                                class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <span class="ml-2 text-gray-700 dark:text-gray-300">{{ user.name }}</span>
                        </label>
                        <div v-if="!checklistManagerUsers || checklistManagerUsers.length === 0" class="col-span-3 text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                            No users with Checklist Manager role found
                        </div>
                    </div>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Selected: {{ form.checklist_manager_ids.length }} manager(s)</p>
                </div>
            </div>

            <!-- Workflow Steps -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Workflow Configuration</h2>

                <!-- Conduct Step (Mandatory) -->
                <div class="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-4 mb-4 rounded">
                    <div class="flex items-start">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3 flex-1">
                            <h3 class="text-sm font-medium text-blue-800 dark:text-blue-200">
                                Step 1: Conduct/Fill Checklist <span class="text-red-500">*</span>
                            </h3>
                            <p class="mt-1 text-xs text-blue-700 dark:text-blue-300">
                                This step is mandatory. Select users who will fill out the checklist.
                            </p>

                            <div class="mt-3 space-y-3">
                                <!-- Assignment Mode -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Assignment Mode <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="form.conduct_assignment_type"
                                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        required
                                    >
                                        <option value="first_to_start">Any assigned can start (first to start locks the checklist)</option>
                                        <option value="all_users">Specific users (all need to complete separately)</option>
                                    </select>
                                </div>

                                <!-- Assigned Users -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Assigned Users <span class="text-red-500">*</span>
                                    </label>
                                    <div class="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2">
                                        <label v-for="user in users" :key="user.id" class="flex items-center text-xs">
                                            <input
                                                v-model="form.conduct_user_ids"
                                                type="checkbox"
                                                :value="user.id"
                                                class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span class="ml-2 text-gray-700 dark:text-gray-300">{{ user.first_name }} {{ user.last_name }}</span>
                                        </label>
                                    </div>
                                    <p v-if="conductUsersError" class="mt-1 text-xs text-red-600 dark:text-red-400">
                                        {{ conductUsersError }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Optional Approval Steps -->
                <div class="mt-6">
                    <div class="flex justify-between items-center mb-4">
                        <div>
                            <h3 class="text-sm font-medium text-gray-900 dark:text-white">Approval Levels (Optional)</h3>
                            <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">Add up to 3 approval levels. Approvals will be executed in sequential order.</p>
                        </div>
                        <button
                            @click="addApprover"
                            type="button"
                            :disabled="activeApprovalCount >= 3"
                            class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Approver
                        </button>
                    </div>

                    <div class="space-y-3">
                        <!-- Approval Level 1 -->
                        <div v-if="form.approval_1_required" class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                            <div class="flex items-start justify-between mb-3">
                                <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                                    Approval Level 1
                                </h4>
                                <button
                                    @click="removeApprover(1)"
                                    type="button"
                                    class="text-red-500 hover:text-red-700"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>

                            <div class="grid grid-cols-1 gap-3">
                                <!-- Step Name -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Step Name (Optional)
                                    </label>
                                    <input
                                        v-model="form.approval_1_step_name"
                                        type="text"
                                        placeholder="e.g., Manager Approval"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    />
                                </div>

                                <!-- Assigned User -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Assigned To <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="form.approval_1_user_id"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        required
                                    >
                                        <option value="">Select User</option>
                                        <option v-for="user in users" :key="user.id" :value="user.id">
                                            {{ user.first_name }} {{ user.last_name }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Auto Approve -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Auto-approve after (Optional)
                                    </label>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                            <input
                                                v-model.number="form.approval_1_auto_approve_hours"
                                                type="number"
                                                min="0"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_1_auto_approve_hours')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                            <input
                                                v-model.number="form.approval_1_auto_approve_minutes"
                                                type="number"
                                                min="0"
                                                max="59"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_1_auto_approve_minutes')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        If set, checklist will be auto-approved after this time if approver hasn't taken action. Leave blank to disable.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Approval Level 2 -->
                        <div v-if="form.approval_2_required" class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                            <div class="flex items-start justify-between mb-3">
                                <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                                    Approval Level 2
                                </h4>
                                <button
                                    @click="removeApprover(2)"
                                    type="button"
                                    class="text-red-500 hover:text-red-700"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>

                            <div class="grid grid-cols-1 gap-3">
                                <!-- Step Name -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Step Name (Optional)
                                    </label>
                                    <input
                                        v-model="form.approval_2_step_name"
                                        type="text"
                                        placeholder="e.g., Director Approval"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    />
                                </div>

                                <!-- Assigned User -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Assigned To <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="form.approval_2_user_id"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        required
                                    >
                                        <option value="">Select User</option>
                                        <option v-for="user in users" :key="user.id" :value="user.id">
                                            {{ user.first_name }} {{ user.last_name }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Auto Approve -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Auto-approve after (Optional)
                                    </label>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                            <input
                                                v-model.number="form.approval_2_auto_approve_hours"
                                                type="number"
                                                min="0"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_2_auto_approve_hours')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                            <input
                                                v-model.number="form.approval_2_auto_approve_minutes"
                                                type="number"
                                                min="0"
                                                max="59"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_2_auto_approve_minutes')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        If set, checklist will be auto-approved after this time if approver hasn't taken action. Leave blank to disable.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Approval Level 3 -->
                        <div v-if="form.approval_3_required" class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                            <div class="flex items-start justify-between mb-3">
                                <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                                    Approval Level 3
                                </h4>
                                <button
                                    @click="removeApprover(3)"
                                    type="button"
                                    class="text-red-500 hover:text-red-700"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>

                            <div class="grid grid-cols-1 gap-3">
                                <!-- Step Name -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Step Name (Optional)
                                    </label>
                                    <input
                                        v-model="form.approval_3_step_name"
                                        type="text"
                                        placeholder="e.g., Executive Approval"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    />
                                </div>

                                <!-- Assigned User -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Assigned To <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="form.approval_3_user_id"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        required
                                    >
                                        <option value="">Select User</option>
                                        <option v-for="user in users" :key="user.id" :value="user.id">
                                            {{ user.first_name }} {{ user.last_name }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Auto Approve -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Auto-approve after (Optional)
                                    </label>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                            <input
                                                v-model.number="form.approval_3_auto_approve_hours"
                                                type="number"
                                                min="0"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_3_auto_approve_hours')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                            <input
                                                v-model.number="form.approval_3_auto_approve_minutes"
                                                type="number"
                                                min="0"
                                                max="59"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_3_auto_approve_minutes')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        If set, checklist will be auto-approved after this time if approver hasn't taken action. Leave blank to disable.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="!form.approval_1_required && !form.approval_2_required && !form.approval_3_required" class="text-center py-6 text-gray-500 dark:text-gray-400 text-sm border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                        No approval levels added. Click "Add Approver" to create optional approval workflow.
                    </div>
                </div>

                <!-- Optional Notifications -->
                <div class="mt-6">
                    <div class="mb-4">
                        <h3 class="text-sm font-medium text-gray-900 dark:text-white">Notifications (Optional)</h3>
                        <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">Select users who will be notified when the checklist is completed (after all conduct and approval steps).</p>
                    </div>

                    <div class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Notify Users
                        </label>
                        <div class="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700">
                            <label v-for="user in users" :key="user.id" class="flex items-center text-xs">
                                <input
                                    v-model="form.notify_user_ids"
                                    type="checkbox"
                                    :value="user.id"
                                    class="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                                />
                                <span class="ml-2 text-gray-700 dark:text-gray-300">{{ user.first_name }} {{ user.last_name }}</span>
                            </label>
                        </div>
                        <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Leave unselected if you don't want to send notifications when checklists are completed.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="flex justify-end space-x-3">
                <Link
                    :href="route('checklists.schedules.show', schedule.id)"
                    class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    Cancel
                </Link>
                <button
                    type="submit"
                    :disabled="form.processing"
                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                    {{ form.processing ? 'Updating...' : 'Update Schedule' }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { computed, ref, watch, onMounted } from 'vue';
import route from '@/Utils/route';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useTimezone } from '@/Composables/useTimezone';

const props = defineProps({
    schedule: Object,
    templates: Array,
    locations: Array,
    assets: Array,
    users: Array,
    checklistManagerUsers: Array,
    errors: Object,
    companyOwner: {
        type: Object,
        default: null,
    },
});

const { toUTC, formatDateTime, timezoneInfo } = useTimezone();

const form = useSecureForm({
    title: '',
    description: '',
    template_id: '',
    recurrence_pattern: 'daily',
    interval: 1,
    start_date: '',
    start_time: '09:00',
    end_date: '',
    sla_hours: 24,
    sla_minutes: 0,
    expires_hours: 0,
    expires_minutes: 0,
    location_id: '',
    asset_id: '',
    checklist_manager_ids: [],

    // Conduct configuration
    conduct_assignment_type: 'first_to_start',
    conduct_user_ids: [],

    // Approval Level 1
    approval_1_required: false,
    approval_1_step_name: '',
    approval_1_user_id: '',
    approval_1_auto_approve_hours: null,
    approval_1_auto_approve_minutes: null,

    // Approval Level 2
    approval_2_required: false,
    approval_2_step_name: '',
    approval_2_user_id: '',
    approval_2_auto_approve_hours: null,
    approval_2_auto_approve_minutes: null,

    // Approval Level 3
    approval_3_required: false,
    approval_3_step_name: '',
    approval_3_user_id: '',
    approval_3_auto_approve_hours: null,
    approval_3_auto_approve_minutes: null,

    // Notifications
    notify_user_ids: [],
});

const conductUsersError = ref('');
const slaError = ref('');
const expiresError = ref('');

// Populate form with existing schedule data
onMounted(() => {
    if (props.schedule) {
        form.title = props.schedule.title || '';
        form.description = props.schedule.description || '';
        form.template_id = props.schedule.template_id || '';
        form.recurrence_pattern = props.schedule.recurrence_pattern || 'daily';
        form.interval = props.schedule.recurrence_config?.interval || 1;

        // Extract date and time from next_execution_at
        if (props.schedule.next_execution_at) {
            const date = new Date(props.schedule.next_execution_at);
            form.start_date = date.toISOString().split('T')[0];
            form.start_time = date.toTimeString().slice(0, 5);
        }

        form.end_date = props.schedule.end_date || '';

        // Convert SLA minutes to hours and minutes
        const totalSlaMinutes = props.schedule.completion_sla_hours || 0;
        form.sla_hours = Math.floor(totalSlaMinutes / 60);
        form.sla_minutes = totalSlaMinutes % 60;

        // Convert expires minutes to hours and minutes
        const totalExpiresMinutes = props.schedule.expires_after_minutes || 0;
        form.expires_hours = Math.floor(totalExpiresMinutes / 60);
        form.expires_minutes = totalExpiresMinutes % 60;

        form.location_id = props.schedule.location_ids?.[0] || '';
        form.asset_id = props.schedule.asset_id || '';
        form.checklist_manager_ids = props.schedule.checklist_manager_ids || [];

        // Conduct configuration
        form.conduct_assignment_type = props.schedule.conduct_assignment_type || 'first_to_start';
        form.conduct_user_ids = props.schedule.conduct_user_ids || [];

        // Approval Level 1
        form.approval_1_required = props.schedule.approval_1_required || false;
        form.approval_1_step_name = props.schedule.approval_1_step_name || '';
        form.approval_1_user_id = props.schedule.approval_1_user_id || '';
        // Convert stored minutes back to hours and minutes
        const totalApproval1Minutes = props.schedule.approval_1_auto_approve_hours || 0;
        form.approval_1_auto_approve_hours = Math.floor(totalApproval1Minutes / 60);
        form.approval_1_auto_approve_minutes = totalApproval1Minutes % 60;

        // Approval Level 2
        form.approval_2_required = props.schedule.approval_2_required || false;
        form.approval_2_step_name = props.schedule.approval_2_step_name || '';
        form.approval_2_user_id = props.schedule.approval_2_user_id || '';
        // Convert stored minutes back to hours and minutes
        const totalApproval2Minutes = props.schedule.approval_2_auto_approve_hours || 0;
        form.approval_2_auto_approve_hours = Math.floor(totalApproval2Minutes / 60);
        form.approval_2_auto_approve_minutes = totalApproval2Minutes % 60;

        // Approval Level 3
        form.approval_3_required = props.schedule.approval_3_required || false;
        form.approval_3_step_name = props.schedule.approval_3_step_name || '';
        form.approval_3_user_id = props.schedule.approval_3_user_id || '';
        // Convert stored minutes back to hours and minutes
        const totalApproval3Minutes = props.schedule.approval_3_auto_approve_hours || 0;
        form.approval_3_auto_approve_hours = Math.floor(totalApproval3Minutes / 60);
        form.approval_3_auto_approve_minutes = totalApproval3Minutes % 60;

        // Notifications
        form.notify_user_ids = props.schedule.notify_user_ids || [];
    }
});

// Count active approval levels
const activeApprovalCount = computed(() => {
    let count = 0;
    if (form.approval_1_required) count++;
    if (form.approval_2_required) count++;
    if (form.approval_3_required) count++;
    return count;
});

// Get interval label based on recurrence pattern
const getIntervalLabel = () => {
    const pattern = form.recurrence_pattern;
    const interval = form.interval;

    const labels = {
        hourly: interval === 1 ? 'hour' : 'hours',
        daily: interval === 1 ? 'day' : 'days',
        weekly: interval === 1 ? 'week' : 'weeks',
        monthly: interval === 1 ? 'month' : 'months',
        yearly: interval === 1 ? 'year' : 'years',
    };

    return labels[pattern] || '';
};

// Calculate next execution preview
const nextExecutionPreview = computed(() => {
    if (!form.start_date || !form.start_time) return null;

    try {
        const dateTime = `${form.start_date}T${form.start_time}`;
        return formatDateTime(dateTime);
    } catch (e) {
        return null;
    }
});

// Reset interval when pattern changes
watch(() => form.recurrence_pattern, () => {
    form.interval = 1;
});

// Add next available approver
const addApprover = () => {
    if (!form.approval_1_required) {
        form.approval_1_required = true;
    } else if (!form.approval_2_required) {
        form.approval_2_required = true;
    } else if (!form.approval_3_required) {
        form.approval_3_required = true;
    }
};

// Remove specific approver and shift remaining approvers
const removeApprover = (level) => {
    if (level === 1) {
        // If removing level 1, shift 2 -> 1 and 3 -> 2
        form.approval_1_required = form.approval_2_required;
        form.approval_1_step_name = form.approval_2_step_name;
        form.approval_1_user_id = form.approval_2_user_id;
        form.approval_1_auto_approve_hours = form.approval_2_auto_approve_hours;
        form.approval_1_auto_approve_minutes = form.approval_2_auto_approve_minutes;

        form.approval_2_required = form.approval_3_required;
        form.approval_2_step_name = form.approval_3_step_name;
        form.approval_2_user_id = form.approval_3_user_id;
        form.approval_2_auto_approve_hours = form.approval_3_auto_approve_hours;
        form.approval_2_auto_approve_minutes = form.approval_3_auto_approve_minutes;

        form.approval_3_required = false;
        form.approval_3_step_name = '';
        form.approval_3_user_id = '';
        form.approval_3_auto_approve_hours = null;
        form.approval_3_auto_approve_minutes = null;
    } else if (level === 2) {
        // If removing level 2, shift 3 -> 2
        form.approval_2_required = form.approval_3_required;
        form.approval_2_step_name = form.approval_3_step_name;
        form.approval_2_user_id = form.approval_3_user_id;
        form.approval_2_auto_approve_hours = form.approval_3_auto_approve_hours;
        form.approval_2_auto_approve_minutes = form.approval_3_auto_approve_minutes;

        form.approval_3_required = false;
        form.approval_3_step_name = '';
        form.approval_3_user_id = '';
        form.approval_3_auto_approve_hours = null;
        form.approval_3_auto_approve_minutes = null;
    } else if (level === 3) {
        // Simply remove level 3
        form.approval_3_required = false;
        form.approval_3_step_name = '';
        form.approval_3_user_id = '';
        form.approval_3_auto_approve_hours = null;
        form.approval_3_auto_approve_minutes = null;
    }
};

// Prevent decimal point from being typed
const preventDecimal = (event) => {
    if (event.key === '.' || event.key === ',') {
        event.preventDefault();
    }
};

// Strip decimal from pasted values
const stripDecimal = (event, fieldName) => {
    const value = event.target.value;
    if (value.includes('.') || value.includes(',')) {
        const intValue = parseInt(value) || 0;
        form[fieldName] = intValue;
    }
};

const submit = () => {
    // Validate conduct users
    if (form.conduct_user_ids.length === 0) {
        conductUsersError.value = 'Please select at least one user for the conduct step';
        return;
    }
    conductUsersError.value = '';

    // Validate SLA hours and minutes
    const slaHours = parseInt(form.sla_hours) || 0;
    const slaMinutes = parseInt(form.sla_minutes) || 0;
    const totalMinutes = (slaHours * 60) + slaMinutes;

    if (totalMinutes <= 0) {
        slaError.value = 'Please enter at least 1 minute for SLA';
        return;
    }
    if (slaMinutes > 59) {
        slaError.value = 'Minutes must be between 0 and 59';
        return;
    }
    slaError.value = '';

    // Validate Expires hours and minutes
    const expiresHours = parseInt(form.expires_hours) || 0;
    const expiresMinutes = parseInt(form.expires_minutes) || 0;
    const totalExpiresMinutes = (expiresHours * 60) + expiresMinutes;

    if (expiresMinutes > 59) {
        expiresError.value = 'Minutes must be between 0 and 59';
        return;
    }
    expiresError.value = '';

    // Validate approval level assignees
    if (form.approval_1_required && !form.approval_1_user_id) {
        alert('Please select a user for Approval Level 1');
        return;
    }
    if (form.approval_2_required && !form.approval_2_user_id) {
        alert('Please select a user for Approval Level 2');
        return;
    }
    if (form.approval_3_required && !form.approval_3_user_id) {
        alert('Please select a user for Approval Level 3');
        return;
    }

    // Validate and calculate auto-approval times
    let approval1AutoApproveMinutes = null;
    let approval2AutoApproveMinutes = null;
    let approval3AutoApproveMinutes = null;

    if (form.approval_1_required) {
        const hours = parseInt(form.approval_1_auto_approve_hours) || 0;
        const minutes = parseInt(form.approval_1_auto_approve_minutes) || 0;
        if (minutes > 59) {
            alert('Auto-approval minutes for Level 1 must be between 0 and 59');
            return;
        }
        const totalAutoApproveMinutes = (hours * 60) + minutes;
        approval1AutoApproveMinutes = totalAutoApproveMinutes > 0 ? totalAutoApproveMinutes : null;
    }

    if (form.approval_2_required) {
        const hours = parseInt(form.approval_2_auto_approve_hours) || 0;
        const minutes = parseInt(form.approval_2_auto_approve_minutes) || 0;
        if (minutes > 59) {
            alert('Auto-approval minutes for Level 2 must be between 0 and 59');
            return;
        }
        const totalAutoApproveMinutes = (hours * 60) + minutes;
        approval2AutoApproveMinutes = totalAutoApproveMinutes > 0 ? totalAutoApproveMinutes : null;
    }

    if (form.approval_3_required) {
        const hours = parseInt(form.approval_3_auto_approve_hours) || 0;
        const minutes = parseInt(form.approval_3_auto_approve_minutes) || 0;
        if (minutes > 59) {
            alert('Auto-approval minutes for Level 3 must be between 0 and 59');
            return;
        }
        const totalAutoApproveMinutes = (hours * 60) + minutes;
        approval3AutoApproveMinutes = totalAutoApproveMinutes > 0 ? totalAutoApproveMinutes : null;
    }

    // Convert start date and time to UTC
    const localDateTime = `${form.start_date}T${form.start_time}`;
    const utcDateTime = toUTC(localDateTime);

    // Calculate total minutes for SLA
    const completionSlaMinutes = totalMinutes;

    // Calculate expires_after_minutes (null if 0)
    const expiresAfterMinutes = totalExpiresMinutes > 0 ? totalExpiresMinutes : null;

    // Update form with computed values before submission
    form.transform((data) => ({
        ...data,
        start_datetime_utc: utcDateTime.toISOString(),
        completion_sla_minutes: completionSlaMinutes,
        expires_after_minutes: expiresAfterMinutes,
        approval_1_auto_approve_hours: approval1AutoApproveMinutes,
        approval_2_auto_approve_hours: approval2AutoApproveMinutes,
        approval_3_auto_approve_hours: approval3AutoApproveMinutes,
    })).put(route('checklists.schedules.update', props.schedule.id), {
        onError: (errors) => {
            console.error('Validation errors:', errors);

            // Display first error to user
            const firstError = Object.values(errors)[0];
            if (firstError) {
                alert(Array.isArray(firstError) ? firstError[0] : firstError);
            } else {
                alert('Failed to update schedule. Please check the form and try again.');
            }
        },
        onSuccess: () => {
            console.log('Schedule updated successfully');
        },
    });
};
</script>
